import { useEffect, useRef, useState } from "react";
import { useInView } from "../../hooks/useInView";
import personalLoanIcon from "../../assets/home-page/loan-options/personal-loan-icon.svg";
import businessBriefcaseIcon from "../../assets/home-page/loan-options/business-briefcase-icon.svg";
import lightningFastIcon from "../../assets/home-page/loan-options/lightning-fast-icon.svg";
import checkmarkIcon from "../../assets/home-page/loan-options/checkmark-icon.svg";
import buttonArrowIcon from "../../assets/home-page/loan-options/button-arrow-icon.svg";
import viewAllArrowIcon from "../../assets/home-page/loan-options/view-all-arrow-icon.svg";
import { siteConfig } from "../../config/site";

interface LoanCard {
  key: string;
  icon: string;
  eyebrow: string;
  titlePrefix: string;
  titleAccent: string;
  subcopy: string;
  features: string[];
  inactiveBg: string;
}

const CARDS: LoanCard[] = [
  {
    key: "travel",
    icon: personalLoanIcon,
    eyebrow: "FLEXIBLE RATE",
    titlePrefix: "Loan for",
    titleAccent: "Travel",
    subcopy: "Funds for the things that matter.",
    features: ["Explore new destinations", "Easy EMIs", "Quick approval"],
    inactiveBg: "#f7f3ff",
  },
  {
    key: "emergency",
    icon: businessBriefcaseIcon,
    eyebrow: "GROWTH TIER",
    titlePrefix: "Loan for",
    titleAccent: "Emergency Needs",
    subcopy: "More funds. More room to grow.",
    features: ["Medical emergencies", "Unexpected expenses", "Hassle-free process"],
    inactiveBg: "#f3edff",
  },
  {
    key: "deals",
    icon: lightningFastIcon,
    eyebrow: "INSTANT REVIEW",
    titlePrefix: "Loan for",
    titleAccent: "Amazing Deals",
    subcopy: "Need funds sooner?",
    features: ["Latest offers & sales", "Upgrade your lifestyle", "Flexible repayment"],
    inactiveBg: "#f7f3ff",
  },
];

// Active card cycles: Middle → Right → Left → repeat.
const SEQUENCE = [1, 2, 0];
const TRANSITION_MS = 800;
const PAUSE_MS = 2500;
// Note: Tailwind's JIT scanner needs literal class strings, so this duration
// (800ms) must stay hardcoded here in sync with TRANSITION_MS above.
const TRANSITION_CLASS = "transition-colors duration-[800ms] ease-in-out";

const GREEN_BG = "#176766";
const GREEN_BORDER = "#1f4d3a";
const PINK_BORDER = "#e7dce8";

export function LoanOptions() {
  const [activeIndex, setActiveIndex] = useState(CARDS.length ? SEQUENCE[0] : 0);
  const seqPos = useRef(0);
  const { ref: headerRef, inView: headerInView } = useInView<HTMLDivElement>();

  useEffect(() => {
    const interval = setInterval(() => {
      seqPos.current = (seqPos.current + 1) % SEQUENCE.length;
      setActiveIndex(SEQUENCE[seqPos.current]);
    }, TRANSITION_MS + PAUSE_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-16 sm:pb-10 sm:pt-24">
      {/* Header row */}
      <div
        ref={headerRef}
        className={`flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end ${headerInView ? "in-view" : ""}`}
      >
        <div className="flex max-w-[672px] flex-col items-start gap-5">
          <div className="reveal-scroll delay-1 flex items-center gap-2 rounded-full border border-solid border-[#e7dce8] bg-white/70 px-[15px] py-[7px] backdrop-blur-[2px]">
            <span className="h-[6px] w-[6px] rounded-full bg-[#176766]" />
            <span className="text-xs font-semibold uppercase tracking-[0.6px] text-[#176766]">
              Our Loan Options
            </span>
          </div>
          <h2 className="reveal-scroll delay-2 tracking-[-1.5px]">
            <span className="block font-inter text-3xl font-light leading-tight text-[rgba(62,2,92,0.85)] sm:text-4xl lg:text-5xl">
              Personalized lending,
            </span>
            <span className="block text-3xl leading-tight text-[#3e025c] sm:text-4xl lg:text-[60px] lg:leading-[60px]">
              Whatever you're planning, we've got a loan for it.
            </span>
          </h2>
        </div>

        <div className="flex max-w-[320px] flex-col items-start gap-5">
          <p className="reveal-scroll delay-3 text-lg leading-7 text-[#57534e]">
            From personal expenses to business growth, explore loan options
            thoughtfully designed around your evolving financial needs.
          </p>
          <a href="#loan-options" className="reveal-scroll delay-4 flex items-center gap-[10px]">
            <span className="text-sm font-semibold text-[#3e025c]">
              View all loan options
            </span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#3e025c] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
              <img src={viewAllArrowIcon} alt="" className="h-[14px] w-[14px]" />
            </span>
          </a>
        </div>
      </div>

      {/* Cards grid */}
      <div className="mt-14 grid grid-cols-1 items-stretch gap-8 md:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((card, index) => {
          const isActive = index === activeIndex;
          const bg = isActive ? GREEN_BG : card.inactiveBg;
          const border = isActive ? GREEN_BORDER : PINK_BORDER;
          const titleColor = isActive ? "#ffffff" : "#0f172a";
          const accentColor = isActive ? "#d88bff" : "#0e605c";
          const subcopyColor = isActive ? "#d6c29e" : "#176766";
          const featureIconBg = isActive ? "#dfa8fa" : "#0e605c";
          const featureTextColor = isActive ? "#ffffff" : "#334155";
          const ctaBg = isActive ? "#ffffff" : "#0e605c";
          const ctaTextColor = isActive ? "#000000" : "#ffffff";

          return (
            <div
              key={card.key}
              className={`relative flex flex-col overflow-hidden rounded-[32px] border border-solid p-6 sm:p-[41px] ${TRANSITION_CLASS}`}
              style={{ backgroundColor: bg, borderColor: border }}
            >
              {/* Decorative glow — fades in only while this card is active */}
              <div
                className={`pointer-events-none absolute h-[224px] w-[224px] rounded-full bg-white/[0.04] blur-[20px] transition-opacity duration-[800ms] ease-in-out ${
                  isActive ? "opacity-100" : "opacity-0"
                }`}
                style={{ top: "-64px", right: "-64.33px" }}
              />

              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl border border-solid ${TRANSITION_CLASS}`}
                    style={{
                      backgroundColor: isActive ? "rgba(255,255,255,0.1)" : "#ffffff",
                      borderColor: isActive ? "rgba(255,255,255,0.15)" : border,
                    }}
                  >
                    <img
                      src={card.icon}
                      alt=""
                      className="h-5 w-5 transition-[filter] duration-[800ms] ease-in-out"
                      style={{ filter: isActive ? "brightness(0) invert(1)" : "brightness(0)" }}
                    />
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.6px] ${TRANSITION_CLASS}`}
                    style={{
                      backgroundColor: isActive ? "rgba(255,255,255,0.1)" : "transparent",
                      color: isActive ? "#ffffff" : "rgba(87,83,78,0.7)",
                    }}
                  >
                    {card.eyebrow}
                  </span>
                </div>

                <h3
                  className={`pt-[30px] text-2xl font-bold leading-[30px] ${TRANSITION_CLASS}`}
                  style={{ color: titleColor }}
                >
                  {card.titlePrefix}{" "}
                  <span className={TRANSITION_CLASS} style={{ color: accentColor }}>
                    {card.titleAccent}
                  </span>
                </h3>
                <p
                  className={`pt-2 text-sm font-semibold tracking-[0.35px] ${TRANSITION_CLASS}`}
                  style={{ color: subcopyColor }}
                >
                  {card.subcopy}
                </p>

                <ul className="mt-6 flex flex-col gap-2">
                  {card.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${TRANSITION_CLASS}`}
                        style={{ backgroundColor: featureIconBg }}
                      >
                        <img src={checkmarkIcon} alt="" className="h-[14px] w-[14px]" />
                      </span>
                      <span
                        className={`text-sm font-semibold ${TRANSITION_CLASS}`}
                        style={{ color: featureTextColor }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href={siteConfig.applyLink || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`relative z-10 mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-full text-base font-bold shadow-[0px_1px_1px_rgba(0,0,0,0.05)] hover:opacity-90 ${TRANSITION_CLASS}`}
                style={{ backgroundColor: ctaBg, color: ctaTextColor }}
              >
                Apply for Loan
                <img
                  src={buttonArrowIcon}
                  alt=""
                  className="h-[3.75px] w-[9.47px] transition-[filter] duration-[800ms] ease-in-out"
                  style={{
                    filter: ctaTextColor === "#ffffff" ? "invert(1)" : "none",
                  }}
                />
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}
